import { Vue, Component, Prop, Emit } from "@wagich/vue-facing-decorator-metadata";

import { render } from "./person-detail.html";
import { type Person, type PersonContact, type PersonFunction, ContactType, ContactTypeNames, Role, RoleNames } from "../models/Person";
import { Fancybox } from "@fancyapps/ui";
import { createApp, type App } from "vue";
import { configureApp } from "~/vue/utils";

export type PersonDetailModal = NonNullable<ReturnType<typeof Fancybox.show>>;
export type PersonDetailModalOptions = NonNullable<Parameters<typeof Fancybox.show>[1]>;

const contactLinkFormatters = new Map<ContactType, (contact: PersonContact) => void>([
	[ContactType.PhoneNumber, contact => {
		var normalizedNumber = contact.value.replace(/\s/g, "");
		return `tel:${normalizedNumber}`;
	}],
	[ContactType.EmailAddress, contact => `mailto:${contact.value}`]
]);

@Component({
	render
})
export class PersonDetail extends Vue {
	contactTypeNames = ContactTypeNames;
	roleNames = RoleNames;

	@Prop()
	person: Person;

	/** The API serializes roles camelCased, so the value is "teacher" (see the team filter categories). */
	isTeacherFunction(func: PersonFunction) {
		return func.role == "teacher";
	}

	formatContactLink(contact: PersonContact) {
		let formatter = contactLinkFormatters.get(contact.type);
		if (formatter != null) {
			return formatter(contact);
		}
		return contact.value;
	}

	static async openFromUrl(url: URL, options?: PersonDetailModalOptions): Promise<PersonDetailModal | undefined> {
		if (!url.pathname.startsWith("/api/persons/")) {
			return undefined;
		}
		const id = url.pathname.split("/").pop(); // Extract person ID from URL
		if (id == null) {
			return undefined;
		}
		return await PersonDetail.open(id, options);
	}

	/** Opens the person in a Fancybox modal; `options` are merged into the Fancybox options. */
	static async open(id: string, options?: PersonDetailModalOptions): Promise<PersonDetailModal | undefined> {
		let response = await fetch(`/api/persons/${id}`);
		if (response.status >= 300) {
			return undefined;
		}
		let data = await response.json();

		let wrapper = document.createElement("div");
		let element = document.createElement("div");
		wrapper.appendChild(element);
		
		const app = createApp(PersonDetail, { person: data });
		configureApp(app);
		app.mount(element);

		var instance = Fancybox.show([
			{
				html: wrapper
			}
		], {
			mainClass: "fancybox-width-limited-wide",
			...options,
		});
		// Fancybox 6 does not reliably return the instance from show(); the newly opened one is the topmost.
		return instance ?? Fancybox.getInstance();
	}
}
